import { Plus, Warehouse, X } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { EditApartmentForm, Property } from './types';

interface EditApartmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  apartment: EditApartmentForm | null;
  onApartmentChange: (apartment: EditApartmentForm | null) => void;
  onSave: () => void;
  properties: Property[];
  t: (key: string) => string;
}

export function EditApartmentDialog({
  open,
  onOpenChange,
  apartment,
  onApartmentChange,
  onSave,
  properties,
  t
}: EditApartmentDialogProps) {
  if (!apartment) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Redigera lägenhet</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>{t('properties')} *</Label>
            <Select
              value={apartment.property_id}
              onValueChange={(value) => onApartmentChange({ ...apartment, property_id: value })}
            >
              <SelectTrigger data-testid="edit-apt-property-select">
                <SelectValue placeholder="Välj fastighet" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((prop) => (
                  <SelectItem key={prop.id} value={prop.id}>
                    {prop.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>{t('apartment_number')} *</Label>
            <Input
              value={apartment.apartment_number || ''}
              onChange={(e) => onApartmentChange({ ...apartment, apartment_number: e.target.value })}
              placeholder="1001"
              className="font-mono"
              data-testid="edit-apt-number-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('floor')}</Label>
              <Input
                type="number"
                value={apartment.floor || ''}
                onChange={(e) => onApartmentChange({ ...apartment, floor: e.target.value })}
                placeholder="1"
                data-testid="edit-apt-floor-input"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('area')}</Label>
              <Input
                type="number"
                value={apartment.area_sqm || ''}
                onChange={(e) => onApartmentChange({ ...apartment, area_sqm: e.target.value })}
                placeholder="75"
                data-testid="edit-apt-area-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label>{t('dwelling_type')}</Label>
            <Select
              value={apartment.dwelling_type || 'apartment'}
              onValueChange={(value) => onApartmentChange({ ...apartment, dwelling_type: value })}
            >
              <SelectTrigger data-testid="edit-apt-type-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="apartment">{t('apartment')}</SelectItem>
                <SelectItem value="house">{t('house')}</SelectItem>
                <SelectItem value="townhouse">{t('townhouse')}</SelectItem>
                <SelectItem value="studio">{t('studio')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Storage Units (Förråd) */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Warehouse size={16} />
              Förråd
            </Label>
            <div className="space-y-2">
              {(apartment.storage_units || []).map((storage, index) => (
                <div key={`edit-storage-${index}-${storage}`} className="flex gap-2 items-center">
                  <Input
                    value={storage}
                    onChange={(e) => {
                      const updated = [...(apartment.storage_units || [])];
                      updated[index] = e.target.value;
                      onApartmentChange({ ...apartment, storage_units: updated });
                    }}
                    placeholder="t.ex. Förråd 101, Cykelrum B"
                    className="flex-1"
                    data-testid={`storage-unit-${index}`}
                  />
                  <button
                    onClick={() => {
                      const updated = (apartment.storage_units || []).filter((_, i) => i !== index);
                      onApartmentChange({ ...apartment, storage_units: updated });
                    }}
                    className="p-2 text-red-500 hover:bg-red-50 rounded"
                    title="Ta bort"
                    type="button"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const updated = [...(apartment.storage_units || []), ''];
                  onApartmentChange({ ...apartment, storage_units: updated });
                }}
                className="w-full"
              >
                <Plus size={14} className="mr-1" />
                Lägg till förråd
              </Button>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={onSave} className="flex-1" data-testid="update-apartment-button">
              Spara
            </Button>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Avbryt
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
